import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../../config/config";
import { getToken } from "../../../utils/localStorageMethods";
import i18n from "../i18n";
import { translateManyClient } from "../../../utils/ItClient";

const apiUrl = config.Url;
const norm = (l) => (l || "en").slice(0, 2);

// Create a baseQuery with auth headers
const baseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}/vehicle`,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = getToken(); // Get token from cookies or storage
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("ngrok-skip-browser-warning", true);
    }
    return headers;
  },
});

export const vehiclesSlice = createApi({
  reducerPath: "vehiclesSlice",
  baseQuery: baseQuery,
  tagTypes: ["vehicles"],
  endpoints: (builder) => ({
    // GET all vehicles with filters
    getAllVehicles: builder.query({
      query: ({
        page,
        limit,
        make_id,
        color_id,
        vehicle_type_id,
        search,
        role,
      }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);

        if (make_id) params.append("make_id", make_id);
        if (color_id) params.append("color_id", color_id);
        if (vehicle_type_id) params.append("vehicle_type_id", vehicle_type_id);
        if (search) params.append("search", search);
        if (role) params.append("role", role);

        const queryString = params.toString();
        return queryString ? `?${queryString}` : "";
      },

      // Dynamic Data Localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const vehiclesData = data?.data?.vehicles ?? [];
          if (!Array.isArray(vehiclesData) || vehiclesData.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const vehicle of vehiclesData) {
            if (vehicle.make.name) bucket.push(vehicle.make.name);
            if (vehicle.model.name) bucket.push(vehicle.model.name);
            if (vehicle.color.name) bucket.push(vehicle.color.name);
            if (vehicle.vehicle_status) bucket.push(vehicle.vehicle_status);
            if (vehicle.vehicle_type.name)
              bucket.push(vehicle.vehicle_type.name);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            vehiclesSlice.util.updateQueryData(
              "getAllVehicles",
              arg,
              (draft) => {
                const arr = draft?.data?.vehicles ?? [];
                arr.forEach((vehicle) => {
                  vehicle.make.name = pick(vehicle.make.name);
                  vehicle.model.name = pick(vehicle.model.name);
                  vehicle.color.name = pick(vehicle.color.name);
                  vehicle.vehicle_type.name = pick(vehicle.vehicle_type.name);
                  vehicle.vehicle_status = pick(vehicle.vehicle_status);
                });
              },
            ),
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["vehicles"],
    }),
    // GET details of a single vehicle by ID
    getVehicleDetails: builder.query({
      query: (id) => `${id}`,
      providesTags: ["vehicles"],
    }),
    // POST a new vehicle
    addVehicles: builder.mutation({
      query: (data) => ({
        url: "",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["vehicles"],
    }),
    // PUT update vehicle by ID
    updateVehicles: builder.mutation({
      query: ({ data, id }) => ({
        url: `${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["vehicles"],
    }),
    // DELETE a vehicle
    deleteVehicle: builder.mutation({
      query: (id) => ({
        url: `${id}`,
        method: "Delete",
      }),
      invalidatesTags: ["vehicles"],
    }),
  }),
});
// Export hooks for usage in components
export const {
  useGetAllVehiclesQuery, // old use
  useGetVehicleDetailsQuery, // old use
  useAddVehiclesMutation, // old use
  useUpdateVehiclesMutation, // old use
  useDeleteVehicleMutation,
} = vehiclesSlice;
